import React from 'react';
import { useBrand } from '../../contexts/BrandContext';

interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
}

const pricingTiers: PricingTier[] = [
  {
    name: 'Freelancer',
    price: '$149',
    period: '/month',
    description: 'For independent consultants',
    features: [
      'Up to 5 active clients',
      'Client isolation & workspaces',
      'Time tracking',
      'Basic billing integration',
      'Project templates',
      'Email support'
    ],
    cta: 'Start Free Trial'
  },
  {
    name: 'Agency',
    price: '$399',
    period: '/month',
    description: 'For growing agencies & consultancies',
    features: [
      'Unlimited clients',
      'Advanced client isolation',
      'Resource allocation matrix',
      'Full billing integration',
      'Utilization dashboards',
      'Revenue forecasting',
      'White-label reports',
      'Priority support'
    ],
    cta: 'Start Free Trial',
    highlighted: true
  }
];

const features = [
  {
    title: 'Client Isolation',
    description: 'Complete data separation between clients with dedicated workspaces. Your clients data is never mixed or exposed.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    )
  },
  {
    title: 'Resource Allocation',
    description: 'Visualize team capacity across all clients. Prevent overallocation and optimize billable utilization rates.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )
  },
  {
    title: 'Billing Integration',
    description: 'Connect with QuickBooks, Xero, FreshBooks, and Stripe. Auto-generate invoices from tracked time and milestones.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    )
  },
  {
    title: 'Revenue Tracking',
    description: 'Real-time visibility into revenue by client, project, and team member. Forecast future revenue with confidence.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  {
    title: 'Utilization Dashboards',
    description: 'Track billable vs non-billable hours across your entire team. Identify capacity for new client work.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
      </svg>
    )
  },
  {
    title: 'Client Portal',
    description: 'Give clients visibility into their projects with branded portals. Share progress, files, and updates securely.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    )
  }
];

const AgencyFlowLanding: React.FC = () => {
  const { primaryColor, brand } = useBrand();
  const brandColor = brand?.Primary_Color || primaryColor || '#F59E0B';

  const handleMarketplaceCTA = () => {
    window.open('https://aws.amazon.com/marketplace/pp/prodview-agencyflow', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <span className="text-2xl font-bold" style={{ color: brandColor }}>
                AgencyFlow
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <a href="#features" className="text-gray-600 hover:text-gray-900">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900">Pricing</a>
              <button
                onClick={handleMarketplaceCTA}
                className="px-4 py-2 rounded-md text-white font-medium"
                style={{ backgroundColor: brandColor }}
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6">
            Multi-Client Project Management for{' '}
            <span style={{ color: brandColor }}>Agencies</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
            Manage multiple clients without the chaos. Client isolation, resource allocation, and billing
            integration built for freelancers, agencies, and consultancies juggling 3+ clients.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleMarketplaceCTA}
              className="px-8 py-4 rounded-lg text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-shadow"
              style={{ backgroundColor: brandColor }}
            >
              Start Free Trial on AWS
            </button>
            <button className="px-8 py-4 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold text-lg hover:border-gray-400 transition-colors">
              Watch Demo
            </button>
          </div>
          <p className="mt-4 text-sm text-gray-500">
            14-day free trial. No credit card required.
          </p>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-600 mb-8">Trusted by agencies worldwide</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center justify-items-center opacity-50">
            <div className="text-2xl font-bold text-gray-400">DigitalCraft</div>
            <div className="text-2xl font-bold text-gray-400">BrandWorks</div>
            <div className="text-2xl font-bold text-gray-400">ConsultPro</div>
            <div className="text-2xl font-bold text-gray-400">DevAgency</div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Built for agencies that manage multiple clients
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From client onboarding to invoicing, AgencyFlow streamlines every aspect of multi-client project management.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${brandColor}20`, color: brandColor }}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Plans that scale with your agency
            </h2>
            <p className="text-xl text-gray-600">
              Start small and grow as you add more clients
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl p-8 ${
                  tier.highlighted
                    ? 'ring-2 shadow-xl'
                    : 'border border-gray-200'
                }`}
                style={tier.highlighted ? { boxShadow: `0 0 0 2px ${brandColor}` } : {}}
              >
                {tier.highlighted && (
                  <span
                    className="inline-block px-3 py-1 text-sm font-semibold text-white rounded-full mb-4"
                    style={{ backgroundColor: brandColor }}
                  >
                    Most Popular
                  </span>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                <p className="text-gray-600 mb-4">{tier.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
                  <span className="text-gray-600">{tier.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <svg className="w-5 h-5 mr-3" style={{ color: brandColor }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={handleMarketplaceCTA}
                  className={`w-full py-3 px-6 rounded-lg font-semibold ${
                    tier.highlighted
                      ? 'text-white'
                      : 'border-2 text-gray-700'
                  }`}
                  style={
                    tier.highlighted
                      ? { backgroundColor: brandColor }
                      : { borderColor: brandColor, color: brandColor }
                  }
                >
                  {tier.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: brandColor }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to streamline your agency operations?
          </h2>
          <p className="text-xl text-white opacity-90 mb-8">
            Join hundreds of agencies using AgencyFlow to manage clients more efficiently.
          </p>
          <button
            onClick={handleMarketplaceCTA}
            className="px-8 py-4 rounded-lg bg-white font-semibold text-lg shadow-lg hover:shadow-xl transition-shadow"
            style={{ color: brandColor }}
          >
            Start Your Free Trial
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <span className="text-xl font-bold text-white">AgencyFlow</span>
              <p className="mt-4 text-sm">
                Multi-client project management for agencies.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Integrations</a></li>
                <li><a href="#" className="hover:text-white">Client Portal</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">About</a></li>
                <li><a href="#" className="hover:text-white">Blog</a></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-sm text-center">
            <p>&copy; {new Date().getFullYear()} AgencyFlow. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AgencyFlowLanding;
